import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {initialize, reduxForm, formValueSelector, Field} from 'redux-form';
import {connect} from 'react-redux';
import {Link} from 'react-router';
import {injectIntl, FormattedMessage} from 'react-intl';

import * as authActions from '../actions/auth';
import * as xlActions from '../actions/xl';
import RegistrationSocials from '../components/RegistrationSocials';
import FlashMessage from '../components/FlashMessage';
import TextInput from '../components/Form/TextInput';
import Footer, {PATHS} from '../components/Footer';
import CheckboxInput from '../components/Form/CheckboxInput';
import DateInput from '../components/Form/DateInput';
import validate from '../validators/forms/registration';
import {reportError} from '../utils/error-reporter';
import Select from '../components/Form/Select';
import {countryMapping} from '../utils/country-mapping';
import AppBlock from '../components/AppBlock';
import BackButton from '../components/BackButton';

import * as types from '../types/index';
import Title from '../components/Form/Title';
import ResendConfirm from '../components/Form/ResendConfirm';

const TYPE2COMPONENT = {
  str: TextInput,
  email: TextInput,
  pass: TextInput,
  date: DateInput,
  select: Select
};

const TYPE2INPUT_TYPE = {
  email: 'text',
  str: 'text',
  pass: 'password',
  date: 'text',
  select: 'text'
};

const FORM_NAME = 'registration-form';

class AuthRegistration extends Component {
  constructor(props) {
    super(props);

    this.state = {
      consent: ''
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.renderFields = this.renderFields.bind(this);
  }

  componentWillMount() {
    const {
      dispatch,
      xl,
      auth: {
        registration: {
          promo_email_agreement,
          values: {email, username}
        }
      }
    } = this.props;
    this.rootWrapperHeight = this.props.xl.blockHeight;
    this.props.dispatch(xlActions.setBlockHeight('auto'));
    AuthRegistration.hideCaptcha(dispatch, xl);

    const initialFormData = {
      email,
      username,
      password: ''
    };

    const country = xl.registrationFields.find(field => field.name === 'country-name');

    if (country && country.value.includes(xl.localization.country)) {
      initialFormData['country-name'] = countryMapping[xl.localization.country];
    }

    if (promo_email_agreement) {
      initialFormData.promo_email_agreement = true;
    }

    dispatch(initialize('registration-form', initialFormData));
  }

  componentWillUnmount() {
    const {dispatch, auth} = this.props;

    dispatch(xlActions.setBlockHeight(this.rootWrapperHeight));

    if (auth.registration.isRegistering) {
      authActions.resetRegistration(dispatch);
    }
  }

  static hideCaptcha(dispatch, xl) {
    if (xl.captcha.visible) {
      dispatch({type: types.HIDE_CAPTCHA});
      if (xl.captcha.value) {
        dispatch({type: types.REMOVE_RECAPTCHA});
      }
    }

    dispatch({type: types.LOGIN_STOP});
  }

  handleChange(event) {
    if (event.target && event.target.name === 'email') {
      authActions.setEmail(this.props.dispatch, event.target.value);
    }
    if (event.target && event.target.name === 'username') {
      authActions.setUsername(this.props.dispatch, event.target.value);
    }
  }

  renderFields(common) {
    const {
      xl: {
        registrationFields,
        localization: {translation}
      },
      intl
    } = this.props;
    const unknownTypes = registrationFields.filter(
      ({type}) => !Object.keys(TYPE2INPUT_TYPE).includes(type)
    );
    if (unknownTypes.length) {
      reportError(Error(`unknown registration types ${unknownTypes}`));
    }

    return registrationFields
      .filter(({type}) => Object.keys(TYPE2INPUT_TYPE).includes(type))
      .map(({name, required, type, value}) => (
        <Field
          {...common}
          lang={translation}
          component={TYPE2COMPONENT[type]}
          key={name}
          name={name}
          required={required}
          type={TYPE2INPUT_TYPE[type]}
          label={intl.messages[`form.${name}`] || name}
          onChange={this.handleChange}
          options={value}
        />
      ));
  }

  handleFormSubmit(values) {
    const {
      dispatch,
      auth: {captchaRegister, registration},
      xl,
      location
    } = this.props;

    if (registration.loading) {
      return;
    }

    const consentState = this.state.consent;
    const {agreement, ...userValues} = values;
    if (consentState !== '') {
      userValues.consent = consentState;
    }

    Object.keys(userValues).forEach(prop => {
      if (
        typeof userValues[prop] === 'object' &&
        Object.prototype.hasOwnProperty.call(userValues[prop], 'value')
      ) {
        userValues[prop] = userValues[prop].value;
      }
    });

    if (Object.hasOwnProperty.call(userValues, 'country-name')) {
      userValues['country-name'] =
        countryMapping[userValues['country-name']] || userValues['country-name'];
    }

    authActions.registrationAsync(
      dispatch,
      userValues,
      location.query,
      xl,
      captchaRegister.success
    );
  }

  successMessage() {
    const {
      xl: {isIFrame},
      intl,
      auth
    } = this.props;
    return (
      <div className="block-content">
        <div className="content-message">
          <div className="message">
            {intl.formatMessage({id: 'auth-reg.complete'}, {email: auth.registration.values.email})}
            <br />
            {intl.formatMessage({id: 'auth-reg.check-spam-folder'})}
          </div>

          <BackButton isIFrame={isIFrame} />
        </div>
      </div>
    );
  }

  static renderPolicyTosLink({privacyUrl, tosUrl, intl}) {
    if (!privacyUrl && !tosUrl) {
      return '';
    }

    if (privacyUrl && tosUrl) {
      return (
        <FormattedMessage
          id="agreement.privacy"
          values={{
            links: (
              <FormattedMessage
                id="agreement.privacy_tos.link"
                values={{
                  privacy: (
                    <a
                      id="privacy_link"
                      href={privacyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {intl.messages['agreement.privacy.link']}
                    </a>
                  ),
                  tos: (
                    <a id="tos_link" href={tosUrl} target="_blank" rel="noopener noreferrer">
                      {intl.messages['agreement.tos.link']}
                    </a>
                  )
                }}
              />
            )
          }}
        />
      );
    }

    const agreementLink = privacyUrl ? 'privacy' : 'tos';
    return (
      <FormattedMessage
        id="agreement.privacy"
        values={{
          links: (
            <a
              id={`${agreementLink}_link`}
              href={privacyUrl || tosUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.messages[`agreement.${agreementLink}.link`]}
            </a>
          )
        }}
      />
    );
  }

  render() {
    const {
      intl,
      auth,
      handleSubmit,
      xl: {socialLinks, canShowSocialsOnRegistration, privacyUrl, consentUrl, cookieUrl, tosUrl}
    } = this.props;

    const displayBack = auth.registration.loading ? 'none' : 'visiblePainted';

    const hasSocialsToShow =
      socialLinks.primary.length > 0 ||
      socialLinks.secondary.length > 0 ||
      socialLinks.other.length > 0;

    return (
      <Fragment>
        {!auth.registration.isRegistering ? (
          <Fragment>
            <AppBlock>
              <Title
                title={intl.messages['auth-reg.title']}
                forceShow={auth.registration.isRegistering}
                backLink={
                  <Link
                    className="back-link"
                    style={{pointerEvents: displayBack}}
                    to={{pathname: '/', search: this.props.location.search}}
                  >
                    {intl.messages['app.back']}
                  </Link>
                }
              />

              <div className="block-content">
                <div>
                  {auth.registration.error !== null && (
                    <FlashMessage type="error" message={auth.registration.error} />
                  )}
                  {auth.captchaRegister.error !== null && (
                    <FlashMessage type="error" message={auth.captchaRegister.error} />
                  )}

                  <Fragment>
                    {canShowSocialsOnRegistration &&
                      hasSocialsToShow && <RegistrationSocials containerClassName="form-group" />}
                    <form className="form-group" onSubmit={handleSubmit(this.handleFormSubmit)}>
                      {this.renderFields({disabled: auth.registration.loading})}

                      <div className="form-group policies">
                        {cookieUrl && (
                          <Fragment>
                            <FormattedMessage
                              id="agreement.cookie"
                              values={{
                                cookie: (
                                  <a
                                    id="cookie_link"
                                    href={cookieUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {intl.messages['agreement.cookie.link']}
                                  </a>
                                )
                              }}
                            />
                            <br />
                          </Fragment>
                        )}
                        {AuthRegistration.renderPolicyTosLink({privacyUrl, tosUrl, intl})}
                      </div>

                      {consentUrl && (
                        <div className="form-group policies">
                          <FormattedMessage
                            id="agreement.consent"
                            values={{
                              consent: (
                                <a
                                  id="consent_link"
                                  href={consentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {intl.messages['agreement.consent.link']}
                                </a>
                              )
                            }}
                          />
                        </div>
                      )}

                      {auth.registration.promo_email_agreement && (
                        <CheckboxInput
                          name="promo_email_agreement"
                          label={intl.messages['form.promo_email']}
                          disabled={auth.registration.loading}
                        />
                      )}

                      {consentUrl ? (
                        <div className="register-buttons">
                          <button
                            type="submit-with-consent"
                            className="form-button button-with-bg"
                            onClick={() => {
                              this.setState({consent: true});
                            }}
                          >
                            {auth.registration.loading
                              ? intl.messages['form.loading']
                              : intl.messages['agreement.accept']}
                          </button>

                          <button
                            type="submit"
                            className="form-button button-outline"
                            onClick={() => {
                              this.setState({consent: false});
                            }}
                          >
                            {auth.registration.loading
                              ? intl.messages['form.loading']
                              : intl.messages['agreement.ignore']}
                          </button>
                        </div>
                      ) : (
                        <button type="submit" className="form-button button-with-bg">
                          {auth.registration.loading
                            ? intl.messages['form.loading']
                            : intl.messages['auth-reg.submit']}
                        </button>
                      )}
                    </form>
                  </Fragment>
                </div>
              </div>
            </AppBlock>
            <Footer links={{[PATHS.HAVE_ACCOUNT]: !auth.registration.isRegistering}} />
          </Fragment>
        ) : (
          <ResendConfirm location={this.props.location} />
        )}
      </Fragment>
    );
  }
}

AuthRegistration.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  xl: PropTypes.object.isRequired,
  agreement: PropTypes.bool.isRequired
};

function mapStateToProps(state) {
  return {
    ...state,
    ...validate(state.xl.registrationFields),
    agreement: formValueSelector(FORM_NAME)(state, 'agreement') || false,
    form: FORM_NAME
  };
}

export default injectIntl(connect(mapStateToProps)(reduxForm()(AuthRegistration)));



// WEBPACK FOOTER //
// ./pages/Registration.jsx